import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

// Create a context with default values and functions
const MIDIContext = createContext({
  midiAccess: null,
  midiInputs: [],
  midiOutputs: [],
  selectedInput: null,
  selectedOutput: null,
  midiInputChannel: 1,
  midiOutputChannel: 1,
  setSelectedInput: () => {},
  setSelectedOutput: () => {},
  setMidiInputChannel: () => {},
  setMidiOutputChannel: () => {},
  requestMIDIAccess: () => {},
  sendMIDI: () => {},
  sendRawMIDI: () => {},
  registerOnMidiMessageReceivedCallback: () => {}
});

export const MIDIProvider = ({ children }) => {
  const [midiAccess, setMidiAccess] = useState(null);
  const [midiInputs, setMidiInputs] = useState([]);
  const [midiOutputs, setMidiOutputs] = useState([]);
  const [selectedInput, setSelectedInput] = useState(null);
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [midiInputChannel, setMidiInputChannel] = useState(1);  // Default input channel (0-indexed)
  const [midiOutputChannel, setMidiOutputChannel] = useState(1); // Default output channel (0-indexed)

  const onMidiMessageReceivedCallbackRef = useRef(null);

  // Function to register a callback to be executed when data is received
    const registerOnMidiMessageReceivedCallback = (callback) => {
      onMidiMessageReceivedCallbackRef.current = callback;
  };

  const selectedOutputRef = useRef(selectedOutput);
  useEffect(() => {
    selectedOutputRef.current = selectedOutput;
  }, [selectedOutput]);

  useEffect(() => {
    if (selectedInput && onMidiMessageReceivedCallbackRef.current) {
      console.log("selectedInput is now set to:", selectedInput.name);
      
      // Clear all previous event callbacks
      midiInputs.forEach((input) => {
        input.onmidimessage = null;
      });

      // Assign the new one
      selectedInput.onmidimessage = onMidiMessageReceivedCallbackRef.current;
    }
  }, [selectedInput, onMidiMessageReceivedCallbackRef]);

  // Function to request MIDI access manually
  const requestMIDIAccess = () => {
    return navigator.requestMIDIAccess()
      .then((access) => {
        setMidiAccess(access);
  
        // Convert the inputs and outputs iterables to arrays
        const inputs = Array.from(access.inputs.values());
        const outputs = Array.from(access.outputs.values());
        setMidiInputs(inputs);
        setMidiOutputs(outputs);
  
        // Optionally auto-select the first available devices
        if (inputs.length > 0) { setSelectedInput(inputs[0]); }
        if (outputs.length > 0) { setSelectedOutput(outputs[0]); }
  
        return true;
      })
      .catch((err) => {
        console.error("Error accessing MIDI devices:", err);
        return false;
      });
  };

  // Function to send MIDI messages
  // status: base status byte (e.g., 0x90 for note on)
  // data1, data2: additional MIDI data (e.g., note number, velocity)
  // timestamp: optional time at which to send the message (default: immediately)
  const sendMIDI = (status, data1, data2, timestamp = 0) => {
    if (!selectedOutput) {
      console.warn('No MIDI output selected.');
      return;
    }
    // MIDI channels are encoded in the lower 4 bits.
    // For example, a note on message for channel 1 (0-indexed channel 0) is 0x90 | 0.
    // Adjust the status byte to include the current output channel.
    selectedOutput.send([status | midiOutputChannel, data1, data2], timestamp);
  };

  const sendRawMIDI = (messageArray) => {
    if (!selectedOutputRef.current) {
      console.warn('No MIDI output selected.');
      return;
    }
    selectedOutputRef.current.send(messageArray, 0); // zero means send immediately
  };

  return (
    <MIDIContext.Provider value={{
      midiAccess,
      midiInputs,
      midiOutputs,
      selectedInput,
      selectedOutput,
      midiInputChannel,
      midiOutputChannel,
      setSelectedInput,
      setSelectedOutput,
      setMidiInputChannel,
      setMidiOutputChannel,
      requestMIDIAccess,
      sendMIDI,
      sendRawMIDI,
      registerOnMidiMessageReceivedCallback
    }}>
      {children}
    </MIDIContext.Provider>
  );
};

export const useMIDI = () => useContext(MIDIContext);